/**
 * ComingSoonPage — the generic placeholder rendered for gated routes
 * whose page component has not yet been built. Two arrival paths:
 *   - Preview reveal: an off route reached via `?preview=` shows this
 *     instead of the redirect-to-dashboard fallback.
 *   - Released placeholder: a route whose flag is on (so the section/
 *     page is visible to normal users) but the page itself isn't built
 *     yet — `routes.tsx` `elementForItem` falls back here when the item
 *     id isn't in `PAGE_OVERRIDES`. The Outreach → Sequences restoration
 *     (PR #236) is the canonical example.
 *
 * Copy is BRANCHED by arrival path (Agent C iter-1 feedback): the
 * preview-reveal arrival keeps the diagnostic "preview mode is on" hint
 * (useful for operators debugging gating state); the released-placeholder
 * arrival uses the generic "we're building it out" wording.
 *
 * Tokenized: token color utilities + `text-*` roles + Tailwind's 4px
 * scale only. No inline styles, no arbitrary `text-[Npx]`.
 */
import { PageHeading } from "@nu-assist/ui";
import { Construction } from "lucide-react";
import { useFeatureAccess } from "../../features/useFeatureAccess";

export interface ComingSoonPageProps {
  /** Human label for the surface (e.g. "Backchannel Tree"). */
  title: string;
}

export function ComingSoonPage({ title }: ComingSoonPageProps) {
  // `previewing` is true iff the active preview tier > 0. Reading it here
  // (rather than via a prop) keeps every call site clean — the routes
  // tree doesn't have to thread context through `elementForItem`.
  const { previewing } = useFeatureAccess();

  return (
    <div data-testid="coming-soon-page" className="p-12">
      <PageHeading
        title={title}
        icon={Construction}
        iconClassName="text-text-muted"
        description={
          <>
            This feature is on the roadmap and not yet available.{" "}
            {previewing
              ? "You're seeing it because preview mode is on."
              : "We're building it out — check back soon."}
          </>
        }
      />
    </div>
  );
}
